import type { ContextType } from '@/types/aiChat';

export type AICoachPageType = 'cv_analysis' | 'cl_analysis' | 'cl_generation' | 'interview';
export type AICoachUserPlan = 'freemium' | 'premium';
export type AICoachErrorType = 'timeout' | 'api_error' | 'rate_limit';

export interface AICoachContext {
  type: ContextType;
  id: number;
}

export interface AICoachBaseProps extends Record<string, unknown> {
  page_type: AICoachPageType;
  analysis_id: string;
  user_plan: AICoachUserPlan;
}

export interface AICoachCoreProps extends Record<string, unknown> {
  page_type: AICoachPageType;
  analysis_id: string;
}

export const mapContextTypeToPageType = (contextType: ContextType): AICoachPageType => {
  switch (contextType) {
    case 'cv-analysis':
      return 'cv_analysis';
    case 'cl-analysis':
      return 'cl_analysis';
    case 'cl-generation':
      return 'cl_generation';
    case 'interview':
    default:
      return 'interview';
  }
};

export const mapPlanToCoachPlan = (plan?: string | null): AICoachUserPlan => {
  return plan === 'freemium' ? 'freemium' : 'premium';
};

export const buildCoachBaseProps = (context: AICoachContext, userPlan?: string | null): AICoachBaseProps => {
  return {
    page_type: mapContextTypeToPageType(context.type),
    analysis_id: String(context.id),
    user_plan: mapPlanToCoachPlan(userPlan),
  };
};

export const buildCoachCoreProps = (context: AICoachContext): AICoachCoreProps => {
  return {
    page_type: mapContextTypeToPageType(context.type),
    analysis_id: String(context.id),
  };
};

export const getDurationSeconds = (startTimeMs: number): number => {
  return Math.max(0, Math.floor((Date.now() - startTimeMs) / 1000));
};

export const classifyCoachErrorType = (error: unknown): AICoachErrorType => {
  if (!(error instanceof Error)) return 'api_error';
  if (error.name === 'AbortError') return 'timeout';
  const message = error.message.toLowerCase();
  if (message.includes('timeout')) return 'timeout';
  if (message.includes('rate')) return 'rate_limit';
  return 'api_error';
};
